import React, { useState } from 'react'
import { SlButton, SlInput } from '@shoelace-style/shoelace/dist/react'
import { useTranslation } from 'contexts/Localization'
import { useUserSlippageTolerance, useUserTransactionTTL } from 'state/user/hooks'
import QuestionHelper from 'components/QuestionHelper'

enum SlippageError {
  InvalidInput = 'InvalidInput',
  RiskyLow = 'RiskyLow',
  RiskyHigh = 'RiskyHigh',
}

enum DeadlineError {
  InvalidInput = 'InvalidInput',
}

const SlippageTabs: React.FC<{ isMobileView: boolean }> = ({ isMobileView }) => {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippageTolerance()
  const [ttl, setTtl] = useUserTransactionTTL()
  const [slippageInput, setSlippageInput] = useState('')
  const [deadlineInput, setDeadlineInput] = useState('')

  const { t } = useTranslation()

  const slippageInputIsValid =
    slippageInput === '' || (userSlippageTolerance / 100).toFixed(2) === Number.parseFloat(slippageInput).toFixed(2)
  const deadlineInputIsValid = deadlineInput === '' || (ttl / 60).toString() === deadlineInput

  let slippageError: SlippageError | undefined
  if (slippageInput !== '' && !slippageInputIsValid) {
    slippageError = SlippageError.InvalidInput
  } else if (slippageInputIsValid && userSlippageTolerance < 50) {
    slippageError = SlippageError.RiskyLow
  } else if (slippageInputIsValid && userSlippageTolerance > 500) {
    slippageError = SlippageError.RiskyHigh
  } else {
    slippageError = undefined
  }

  let deadlineError: DeadlineError | undefined
  if (deadlineInput !== '' && !deadlineInputIsValid) {
    deadlineError = DeadlineError.InvalidInput
  } else {
    deadlineError = undefined
  }

  const parseCustomSlippage = (value: string) => {
    setSlippageInput(value)

    try {
      const valueAsIntFromRoundedFloat = Number.parseInt((Number.parseFloat(value) * 100).toString())
      if (!Number.isNaN(valueAsIntFromRoundedFloat) && valueAsIntFromRoundedFloat < 5000) {
        setUserSlippageTolerance(valueAsIntFromRoundedFloat)
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  const parseCustomDeadline = (value: string) => {
    setDeadlineInput(value)

    try {
      const valueAsInt: number = Number.parseInt(value) * 60
      if (!Number.isNaN(valueAsInt) && valueAsInt > 0) {
        setTtl(valueAsInt)
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <div className="flex mb-12 items-center">
          <p className="text-white text-14 font-medium">{t('Slippage Tolerance')}</p>
          <QuestionHelper
            content={t(
              'Setting a high slippage tolerance can help transactions succeed, but you may not get such a good price. Use with caution.',
            )}
            placement="top-start"
          />
        </div>
        <div className={isMobileView ? 'flex flex-col gap-12' : 'grid grid-cols-[1fr_2fr] gap-12'}>
          <div className="flex gap-8">
            <SlButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(10)
              }}
              variant={userSlippageTolerance === 10 ? 'primary' : 'neutral'}
            >
              0.1%
            </SlButton>
            <SlButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(50)
              }}
              variant={userSlippageTolerance === 50 ? 'primary' : 'neutral'}
            >
              0.5%
            </SlButton>
            <SlButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(100)
              }}
              variant={userSlippageTolerance === 100 ? 'primary' : 'neutral'}
            >
              1%
            </SlButton>
          </div>
          <SlInput
            className={isMobileView ? '' : 'max-w-[200px]'}
            type="number"
            noSpinButtons
            placeholder={(userSlippageTolerance / 100).toFixed(2)}
            value={slippageInput}
            onSlBlur={() => {
              parseCustomSlippage((userSlippageTolerance / 100).toFixed(2))
            }}
            onSlInput={(e: any) => parseCustomSlippage(e.target.value)}
            invalid={!slippageInputIsValid}
          >
            <div className="text-dark-gray-100 text-14" slot="suffix">
              %
            </div>
            {!!slippageError && (
              <div
                slot="help-text"
                className={`${slippageError === SlippageError.InvalidInput ? 'text-red' : 'text-orange-600'}`}
              >
                {slippageError === SlippageError.InvalidInput
                  ? t('Enter a valid slippage percentage')
                  : slippageError === SlippageError.RiskyLow
                    ? t('Your transaction may fail')
                    : t('Your transaction may be frontrun')}
              </div>
            )}
          </SlInput>

          <div className="flex items-center">
            <p className="text-white text-14 font-semibold">{t('Tx deadline (mins)')}</p>
            <QuestionHelper
              content={t('Your transaction will revert if it is left confirming for longer than this time.')}
              placement="top-start"
            />
          </div>

          <SlInput
            className={isMobileView ? '' : 'max-w-[200px]'}
            type="number"
            noSpinButtons
            onSlBlur={() => {
              parseCustomDeadline((ttl / 60).toString())
            }}
            placeholder={(ttl / 60).toString()}
            value={deadlineInput}
            onSlInput={(e: any) => parseCustomDeadline(e.target.value)}
            invalid={!deadlineInputIsValid}
          >
            <div className="text-dark-gray-100 text-14" slot="suffix">
              {t('mins')}
            </div>
            {!!deadlineError && (
              <div slot="help-text" className="text-red">
                {t('Enter a valid deadline')}
              </div>
            )}
          </SlInput>
        </div>
      </div>
    </div>
  )
}

export default SlippageTabs
